import { useReveal } from "../hooks/useReveal";
import { TESTIMONIALS } from "../lib/constants";
import { Quote, Sparkles, Users, MessageCircle, HeartHandshake } from "lucide-react";

const PILLARS = [
  { icon: HeartHandshake, title: "Relationship-Based Care", body: "Care is more than completing tasks. It's about building trust and meaningful relationships that last." },
  { icon: Users, title: "Consistent Caregivers", body: "Families deserve familiar faces and dependable support — the same trusted person, every visit." },
  { icon: Sparkles, title: "Personalized Support", body: "Every person has different needs. Care should be designed around the individual — never a template." },
  { icon: MessageCircle, title: "Family Communication", body: "Families deserve confidence, regular updates, and real peace of mind throughout the journey." },
];

export default function WhyUs() {
  const [ref, visible] = useReveal();
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="py-24 md:py-32 bg-[#005165] text-[#FFFFFF] relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 20% 20%, #AC9667 0%, transparent 40%), radial-gradient(circle at 80% 80%, #C4B088 0%, transparent 40%)'
      }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <span className="gold-divider mb-5" style={{ color: "#C4B088" }}>Why Families Choose Us</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.08]">
            The peace of mind families deserve.
            <span className="block italic text-[#C4B088]">The dignity seniors have earned.</span>
          </h2>
        </div>

        <div ref={ref} className={`grid md:grid-cols-2 gap-6 ${visible ? 'reveal visible' : 'opacity-0'}`}>
          {PILLARS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`why-pillar-${i}`}
              className="group relative p-7 rounded-2xl border border-[#FFFFFF]/10 bg-[#FFFFFF]/[0.03] hover:bg-[#FFFFFF]/[0.06] transition-all duration-500"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#AC9667]/15 mb-5 border border-[#AC9667]/30">
                <p.icon className="w-5 h-5 text-[#C4B088]" strokeWidth={1.6} />
              </div>
              <h3 className="font-serif text-2xl mb-2">{p.title}</h3>
              <p className="text-[#FFFFFF]/70 text-[15px] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="mt-20">
          <h3 className="font-serif text-2xl md:text-3xl text-[#FFFFFF] mb-8">
            Stories from Madison families
          </h3>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="p-7 rounded-2xl bg-[#FFFFFF]/[0.05] border border-[#FFFFFF]/10 backdrop-blur"
              >
                <Quote className="w-6 h-6 text-[#C4B088] mb-4" />
                <blockquote className="font-serif italic text-[20px] leading-snug text-[#FFFFFF] mb-5">
                  "{t.quote}"
                </blockquote>
                <figcaption className="text-sm">
                  <span className="text-[#C4B088]">{t.name}</span>
                  <span className="text-[#FFFFFF]/60"> · {t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
